import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { PlugZap, ScanSearch, Play, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, usePolling } from "@/lib/api";
import { PageHeader, EmptyState, Spinner, Alert, WorkingTerminal } from "@/components/common";
import AuditReport from "@/components/audit/AuditReport";

const ConnectFirst = ({ status }) => (
  <EmptyState testid="audit-connect-first" icon={PlugZap} title="Connect Google Ads first"
    description="The audit learns from your live campaigns, so it needs real Google Ads data. No data means no audit — we won’t make numbers up.">
    <div className="font-mono text-sm text-red-300">{status.label}</div>
    {status.missing.length > 0 && <ul className="space-y-1">{status.missing.map((m) => <li key={m} className="flex items-center gap-2 font-mono text-xs text-slate-400"><XCircle className="h-3.5 w-3.5 text-red-400" />{m}</li>)}</ul>}
    <Link to="/settings" data-testid="audit-go-settings-button" className="cc-btn cc-btn-primary">Go to Connect & Data</Link>
  </EmptyState>
);

export default function Audit() {
  const { status } = useOutletContext();
  const [audits, setAudits] = useState([]);
  const [current, setCurrent] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [liveNegatives, setLiveNegatives] = useState(null);
  useEffect(() => {
    if (status?.connected) api.get("/google-ads/snapshot").then((r) => setLiveNegatives(r.data.snapshot?.negatives || [])).catch(() => {});
  }, [status?.connected]);

  const loadList = useCallback(async () => {
    const { data } = await api.get("/audits");
    setAudits(data.audits);
    return data.audits;
  }, []);

  const loadOne = useCallback(async (id) => {
    const { data } = await api.get(`/audits/${id}`);
    setCurrent(data);
  }, []);

  useEffect(() => {
    loadList().then((list) => list[0] && loadOne(list[0].id));
  }, [loadList, loadOne]);

  const generating = current?.status === "generating";
  const refresh = useCallback(() => current && loadOne(current.id).then(loadList), [current, loadOne, loadList]);
  usePolling(refresh, generating, 4000);

  const run = async () => {
    setStarting(true);
    setError("");
    try {
      const { data } = await api.post("/audits");
      setCurrent(data);
      loadList();
      toast.message("Data pulled. Claude is auditing the account…");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setStarting(false);
    }
  };

  if (!status) return <Spinner />;
  const ready = status.connected && status.selected_account;

  return (
    <div data-testid="audit-page">
      <PageHeader eyebrow="Learn from ongoing campaigns" title="Account Audit"
        description="Claude Sonnet 5.5 reads the last 30 days of your Google Ads data plus your Business Profile and reports what’s burning budget, what’s working and what to fix first."
        actions={ready && (
          <>
            {audits.length > 1 && (
              <select data-testid="audit-history-select" className="cc-input w-auto font-mono text-xs" value={current?.id || ""} onChange={(e) => loadOne(e.target.value)}>
                {audits.map((a) => <option key={a.id} value={a.id}>{fmtDate(a.created_at)} · {a.status}</option>)}
              </select>
            )}
            <button data-testid="run-audit-button" onClick={run} disabled={starting || generating} className="cc-btn cc-btn-primary">
              {starting ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <Play className="h-4 w-4" />} Run new audit
            </button>
          </>
        )} />
      {error && <div className="mb-6"><Alert testid="audit-error" title="Audit could not start">{error}</Alert></div>}
      {!status.connected && <ConnectFirst status={status} />}
      {status.connected && !status.selected_account && (
        <EmptyState testid="audit-select-account" icon={PlugZap} title="Pick an account" description="Google Ads is connected. Choose which account to audit on the Connect & Data page.">
          <Link to="/settings" className="cc-btn cc-btn-primary">Choose account</Link>
        </EmptyState>
      )}
      {ready && !current && <EmptyState testid="audit-empty-state" icon={ScanSearch} title="No audit yet" description={`Run the first audit of ${status.selected_account.name}. It pulls fresh data, then Claude analyses it (about a minute).`} />}
      {ready && generating && <WorkingTerminal testid="audit-progress" title="Claude Sonnet 5.5 is auditing your account" lines={["Last 30 days pulled from Google Ads", "Scanning search terms for wasted spend", "Inferring targeting and quick wins"]} />}
      {current?.status === "error" && <Alert testid="audit-failed" title="Audit failed">{current.error}</Alert>}
      {current?.status === "ready" && (
        <>
          <div className="mb-4 font-mono text-xs text-slate-500" data-testid="audit-meta">{current.account_name} · {fmtDate(current.completed_at)} · {current.model}</div>
          <AuditReport result={current.result} currency={status.selected_account?.currency} liveNegatives={liveNegatives}
            onSaveNegatives={async (items) => {
              try { const { data } = await api.put(`/audits/${current.id}/negatives`, { items }); setCurrent({ ...current, result: { ...current.result, negative_keyword_suggestions: data.items } }); toast.success("Negative keyword ideas saved"); }
              catch (e) { toast.error(errMsg(e)); }
            }} />
        </>
      )}
    </div>
  );
}
